/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect } from 'react';

/* Import configuration */
import { APP_NAME, APP_TITLES } from '@/config/app';

/* Import hooks */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import type { DashboardBreadcrumbItem } from '@/components/layout/DashboardBreadcrumb';

/* Import types */
import type { ActiveTab } from '@/types/ui';

interface UseDocumentTitleOptions {
    activeTab: ActiveTab;
    breadcrumbItems: DashboardBreadcrumbItem[];
    hasSession: boolean;
}

export function useDocumentTitle({
    activeTab,
    breadcrumbItems,
    hasSession,
}: UseDocumentTitleOptions) {
    const { language } = useI18n();

    useEffect(() => {
        if (!hasSession) {
            document.title = APP_TITLES[language];
            return;
        }

        if (activeTab === 'home') {
            document.title = APP_NAME;
            return;
        }

        const breadcrumbPath = breadcrumbItems
            .map((item) => item.label)
            .filter(Boolean)
            .join(' > ');

        document.title = breadcrumbPath
            ? `${APP_NAME} > ${breadcrumbPath}`
            : APP_NAME;
    }, [activeTab, breadcrumbItems, hasSession, language]);
}
